import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { compressChatImage, isAcceptedChatImageType } from './compressChatImage';
import { MAX_CHAT_IMAGE_ATTACHMENTS, type PendingChatImageAttachment } from './chatImageAttachment';

type UseChatImageAttachments = {
  attachments: PendingChatImageAttachment[];
  // ファイルを圧縮して添付に追加する（上限・非対応形式・サイズ超過はトーストで通知）
  addFiles: (files: File[]) => Promise<void>;
  removeAttachment: (id: string) => void;
  clear: () => void;
  isProcessing: boolean;
};

/**
 * チャット入力欄の画像添付状態を管理するフック。
 * 追加時に圧縮し、ObjectURL の生成・解放（削除・クリア・アンマウント）まで面倒を見る。
 *
 * @param getReservedSlots ファイルなど他種の添付が占有している枠数を返す関数（合計上限の算出に使う）。
 *   関数で受け取ることで、ファイルフックとの相互参照（TDZ）を避けつつ最新の枚数を参照する。
 */
export function useChatImageAttachments(getReservedSlots: () => number = () => 0): UseChatImageAttachments {
  const { t } = useTranslation();
  const [attachments, setAttachments] = useState<PendingChatImageAttachment[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // アンマウント時の revoke で最新の添付一覧を参照するための ref
  const attachmentsRef = useRef<PendingChatImageAttachment[]>(attachments);
  attachmentsRef.current = attachments;

  // 実行中の addFiles 数。並行呼び出し時に、先に終わった呼び出しの finally が
  // isProcessing を false に戻してしまわないよう、全件完了時にだけ false にする。
  const inFlightCountRef = useRef(0);

  const addFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      // 以下の検証はすべて all-or-nothing。非対応形式・上限超過・圧縮失敗が1件でもあれば
      // 「対応分だけ添付」はせず全件中止する。一部だけ添付される直感に反する結果を避ける。
      // 画像単体の上限と、画像+ファイル合算の上限（getReservedSlots）の両方を満たす残り枠。
      const remaining = MAX_CHAT_IMAGE_ATTACHMENTS - getReservedSlots() - attachmentsRef.current.length;
      if (files.length > remaining) {
        toast.error(t('viewModel.aiChat.imageCountLimit', { max: MAX_CHAT_IMAGE_ATTACHMENTS }));
        return;
      }

      // 非対応拡張子が1件でも含まれていたら、圧縮に入る前に全件中止する。
      if (files.some((file) => !isAcceptedChatImageType(file.type))) {
        toast.error(t('viewModel.aiChat.imageUnsupportedDropped'));
        return;
      }

      inFlightCountRef.current += 1;
      setIsProcessing(true);
      try {
        // 全ファイルを圧縮し終えてから一括で追加する。1件でも失敗（デコード失敗等）したら、
        // それまでに生成した ObjectURL を解放し、何も添付せず中止する。
        const compressed: PendingChatImageAttachment[] = [];
        for (const file of files) {
          const result = await compressChatImage(file);
          if (result.type === 'error') {
            compressed.forEach((attachment) => URL.revokeObjectURL(attachment.previewUrl));
            toast.error(t(result.key, result.params));
            return;
          }
          compressed.push({
            id: uuidv4(),
            filename: file.name,
            mediaType: result.mediaType,
            blob: result.blob,
            previewUrl: URL.createObjectURL(result.blob),
          });
        }
        // 圧縮中に他の追加で枠が埋まっていたらバッチごと捨てる（並行追加対策）
        setAttachments((prev) => {
          if (prev.length + getReservedSlots() + compressed.length > MAX_CHAT_IMAGE_ATTACHMENTS) {
            compressed.forEach((attachment) => URL.revokeObjectURL(attachment.previewUrl));
            return prev;
          }
          return [...prev, ...compressed];
        });
      } finally {
        inFlightCountRef.current -= 1;
        if (inFlightCountRef.current === 0) {
          setIsProcessing(false);
        }
      }
    },
    [t, getReservedSlots]
  );

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const target = prev.find((attachment) => attachment.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((attachment) => attachment.id !== id);
    });
  }, []);

  const clear = useCallback(() => {
    setAttachments((prev) => {
      prev.forEach((attachment) => URL.revokeObjectURL(attachment.previewUrl));
      return [];
    });
  }, []);

  useEffect(() => {
    return () => {
      attachmentsRef.current.forEach((attachment) => URL.revokeObjectURL(attachment.previewUrl));
    };
  }, []);

  return { attachments, addFiles, removeAttachment, clear, isProcessing };
}
